import React from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export type StatTone =
  | "indigo"
  | "sky"
  | "amber"
  | "emerald"
  | "orange"
  | "rose";

/** /10 plate + /20 border + 500 ink — one triple per status, flips with the theme. */
const TONES: Record<StatTone, { plate: string; ink: string; meter: string }> = {
  indigo: {
    plate: "bg-indigo-500/10 border-indigo-500/20",
    ink: "text-indigo-500",
    meter: "bg-indigo-500",
  },
  sky: {
    plate: "bg-sky-500/10 border-sky-500/20",
    ink: "text-sky-500",
    meter: "bg-sky-500",
  },
  amber: {
    plate: "bg-amber-500/10 border-amber-500/20",
    ink: "text-amber-500",
    meter: "bg-amber-500",
  },
  emerald: {
    plate: "bg-emerald-500/10 border-emerald-500/20",
    ink: "text-emerald-500",
    meter: "bg-emerald-500",
  },
  orange: {
    plate: "bg-orange-500/10 border-orange-500/20",
    ink: "text-orange-500",
    meter: "bg-orange-500",
  },
  rose: {
    plate: "bg-rose-500/10 border-rose-500/20",
    ink: "text-rose-500",
    meter: "bg-rose-500",
  },
};

/** A count, at the top of the type scale, always tabular so tiles align. */
export const StatNumber: React.FC<{ value: number }> = ({ value }) => (
  <p className="text-[22px] font-bold leading-tight tabular-nums text-text-primary">
    {value.toLocaleString()}
  </p>
);

/** How much of the whole this stat accounts for. Silent when there is no whole. */
export const ShareMeter: React.FC<{
  value: number;
  total: number;
  tone: StatTone;
  className?: string;
}> = ({ value, total, tone, className }) => {
  const share = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div className={clsx("flex items-center gap-2", className)}>
      <div
        className="h-1 flex-1 overflow-hidden rounded-full bg-overlay-mid"
        role="presentation"
      >
        <div
          className={clsx(
            "h-full rounded-full transition-[width] duration-500 ease-out",
            TONES[tone].meter
          )}
          style={{ width: `${share}%` }}
        />
      </div>
      <span className="text-[11px] font-bold tabular-nums tracking-[0.08em] text-light-gray">
        {share}%
      </span>
    </div>
  );
};

export const StatTile: React.FC<{
  label: string;
  value: number;
  total: number;
  icon: IconDefinition;
  tone: StatTone;
}> = ({ label, value, total, icon, tone }) => (
  <div className="rounded-2xl border border-edge bg-surface p-4 shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:border-edge-strong hover:shadow-glow-sm">
    <div className="flex items-start gap-3">
      <span
        className={clsx(
          "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border",
          TONES[tone].plate
        )}
      >
        <FontAwesomeIcon
          icon={icon}
          className={clsx("text-[15px]", TONES[tone].ink)}
        />
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] font-bold uppercase tracking-[0.08em] text-light-gray">
          {label}
        </p>
        <StatNumber value={value} />
      </div>
    </div>

    <ShareMeter value={value} total={total} tone={tone} className="mt-3" />
  </div>
);

/** Sized to the tile it replaces, so the grid does not reflow on load. */
export const StatTileSkeleton: React.FC = () => (
  <div className="rounded-2xl border border-edge-faint bg-surface p-4">
    <div className="flex items-start gap-3">
      <div className="h-11 w-11 shrink-0 animate-pulse rounded-xl bg-skeleton" />
      <div className="flex-1 space-y-2 pt-0.5">
        <div className="h-3 w-2/3 animate-pulse rounded bg-skeleton" />
        <div className="h-6 w-1/3 animate-pulse rounded bg-skeleton" />
      </div>
    </div>
    <div className="mt-3 h-1 animate-pulse rounded-full bg-skeleton" />
  </div>
);
